import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { opacityInOut } from '../../animations/opacity-in-out.animation';
import { scaleInOut } from '../../animations/scale-in-out.animation';
import { UserProxy } from 'src/app/core/store/user/user.proxy';
import { BehaviorSubject, Subject, Subscription, delay, skip, take, takeUntil } from 'rxjs';
import { ApiService } from 'src/app/core/services/api.service';
import { HelperService } from 'src/app/core/services/helper.service';
import { getWindow } from 'ssr-window';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { GA_EVENTS } from 'src/app/core/const.variables';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'files-manager',
  templateUrl: 'files-manager.component.html',
  styleUrls: ['files-manager.component.scss'],
  animations: [ opacityInOut, scaleInOut ]
})
export class FilesManagerComponent implements OnInit, OnDestroy {

  @Input() isAttach = false;
  @Input() parseCv = false;
  @Input() skipDefaultCv = false;
  @Input() isTalent = false;
  @Output() onClose = new EventEmitter();
  @Output() onDelete = new EventEmitter();
  @Output() onUploadDone = new EventEmitter();
  @ViewChild('fileUpload') fileUpload: ElementRef;
  possibleExt = ['.doc', '.docx', '.ppt', '.pptx', '.txt', '.pdf', '.rtl', '.odt', '.png', '.jpeg', '.jpg'];
  fileLoadingProccess = null;
  totalStorageSize = 0;
  showError = null;
  selectedFile = null;
  fileForDelete = null;
  userFiles = [];
  downloadInProgress = false;
  blockView = false;
  defaultCv = new FormControl(null);
  defaultCvFiles = [];
  talentDefaultCv$ = this.userProxy.talentDefaultCv$;
  savingDefaultCv = false;
  setDefaultCvNotify = null;
  private userFilesSub: Subscription;

  constructor(
    private userProxy: UserProxy,
    private apiService: ApiService,
    private helperService: HelperService,
    private gaService: GoogleAnalyticsService,
  ) {
  }

  ngOnInit(): void {
    if (this.parseCv) {
      this.possibleExt = ['.doc', '.docx', '.pdf'];
    }
    if (this.isAttach) {
      this.helperService.blockScroll$.next(true);
    }
    if (this.isTalent) {
      this.userProxy.talentDefaultCv$.pipe(take(1)).subscribe(defaultCv => {
        if (defaultCv && defaultCv.fileUrl) {
          this.defaultCv.setValue(defaultCv.fileUrl);
        }
      });
    }
    this.userFilesSub = this.userProxy.userFiles$.subscribe(files => {
      this.userFiles = files;
      if (files && files.length) {
        this.defaultCvFiles = this.getDefaultCvFiles(files);
        this.totalStorageSize = 0;
        files.map(item => this.totalStorageSize = this.totalStorageSize + item.size);
      } else {
        this.defaultCvFiles = [];
        this.totalStorageSize = 0;
      }
    });
  }

  close(hasAttachments = false) {
    this.onClose.emit(hasAttachments ? this.selectedFile : false);
  }

  setupFocusListener() {
    this.blockView = true;
    getWindow().addEventListener('focus', (e) => {
      if (this.blockView) {
        setTimeout(() => this.blockView = false, 1000);
      }
    }, { once: true })
  }

  checkDefaultCv(e) {
    this.savingDefaultCv = true;
    setTimeout(() => this.savingDefaultCv = false, 1000);
    if (this.defaultCv.value) {
      const selectedFile = this.userFiles.find(val => val.fileUrl === this.defaultCv.value);
      this.userProxy.setTalentDefaultCv({ fileUrl: selectedFile.fileUrl, type: selectedFile.type });
      this.apiService.setDefaultCv(selectedFile.fileUrl, selectedFile.type).subscribe();
    } else {
      this.userProxy.setTalentDefaultCv(null);
      this.apiService.removeDefaultCv().subscribe();
    }
  }

  onFileChange(e, isParse = false) {
    if (e.target && e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
  
      const reader = new FileReader();
  
      reader.onload = () => {
        const base64String = btoa(
          new Uint8Array(reader.result as ArrayBuffer)
            .reduce((data, byte) => data + String.fromCharCode(byte), '')
        );
        const payload = {
          name: file.name,
          size: file.size,
          type: file.type,
          base64String: base64String,
          details: {
            type: isParse ? 'cv' : null
          }
        };
        if (payload.size === 0) {
          this.showError = 'Empty files are not allowed.';
        }
        if (payload.size > 1024 * 1024 * 6) {
          this.showError = 'The file must not exceed 6MB.';
        }
        if ((payload.size + this.totalStorageSize) > 1024 * 1024 * 30) {
          this.showError = 'Maximum storage capacity 30MB.';
        }
        if (this.userFiles && this.userFiles.some(v => v.name === payload.name)) {
          this.showError = 'Document with such name already exists.';
        }
        const fileExt = payload.name.toLowerCase().split('.').pop();
        if (!fileExt || (!this.possibleExt.includes('.' + fileExt))) {
          this.showError = `Only ${this.possibleExt.join(', ') } is accepted.`;
        }
        if (this.showError) {
          this.onUploadDone.emit(this.showError);
          this.fileUpload.nativeElement.value = '';
          setTimeout(() => this.showError = null, 5000);
          return;
        }

        this.fileLoadingProccess = 100;
        const uploadFinished$ = new BehaviorSubject<any>(null);

        this.userProxy.uploadUserFile(payload).then(response => {
          if (this.fileLoadingProccess === 100) {
            this.fileUpload.nativeElement.value = '';
          }
          this.fileLoadingProccess = null;
          const fileUrl = response?.payload?.fileUrl ?? null;
          uploadFinished$.next(fileUrl);

          if (!fileUrl) {
            this.showError = 'File upload failed.';
            this.onUploadDone.emit(this.showError);
            this.fileLoadingProccess = null;
            return;
          }

          const uploadFinishSub = uploadFinished$.subscribe(fileUrl => {
            const finisUploading = () => {
              uploadFinishSub.unsubscribe();
              this.fileLoadingProccess = null;
              // setTimeout(() => this.fileLoadingProccess = null, 500);
              this.onUploadDone.emit(null);
            }
            if (fileUrl) {
              this.userProxy.getUser().then(() => {
                finisUploading();
                if (isParse) {
                  this.gaService.gtag('event', GA_EVENTS.CvParseRequested);
                  if (!this.skipDefaultCv) {
                    this.userProxy.setTalentDefaultCv({ fileUrl, type: payload.type });
                    this.apiService.setDefaultCv(fileUrl, payload.type).subscribe();
                  }
                } else {
                  if (payload.name && /\.(pdf|doc|docx)$/.test(payload.name) && !localStorage.getItem('noDefaultCvNotify')) {
                    this.setDefaultCvNotify = { fileUrl, type: payload.type };
                  }
                }
              });
            } else {
              finisUploading();
            }
          });
        }).catch(err => {
        this.showError = 'An error occurred during file upload.';
        this.onUploadDone.emit(this.showError);
        this.fileLoadingProccess = null;
      });
        // const fileLoadingStateSub = this.userProxy.fileLoadingState$.subscribe(result => {
        //   this.fileLoadingProccess = result;
        //   if (this.fileLoadingProccess === 100) {
        //     this.fileUpload.nativeElement.value = '';
        //     // setTimeout(() => this.fileLoadingProccess = 0);
        //     fileLoadingStateSub.unsubscribe();

        //     const uploadFinishSub = uploadFinished$.pipe(skip(1)).subscribe(fileUrl => {
        //       const finisUploading = () => {
        //         uploadFinishSub.unsubscribe();
        //         this.fileLoadingProccess = null;
        //         // setTimeout(() => this.fileLoadingProccess = null, 500);
        //         this.onUploadDone.emit(null);
        //       }
        //       if (fileUrl) {
        //         this.userProxy.getUser().then(() => {
        //           finisUploading();
        //           if (isParse) {
        //             this.gaService.gtag('event', GA_EVENTS.CvParseRequested);
        //             if (!this.skipDefaultCv) {
        //               this.userProxy.setTalentDefaultCv({ fileUrl, type: payload.type });
        //               this.apiService.setDefaultCv(fileUrl, payload.type).subscribe();
        //             }
        //           } else {
        //             if (payload.name && /\.(pdf|doc|docx)$/.test(payload.name) && !localStorage.getItem('noDefaultCvNotify')) {
        //               this.setDefaultCvNotify = { fileUrl, type: payload.type };
        //             }
        //           }
        //         });
        //       } else {
        //         finisUploading();
        //       }
        //     });
        //   }
        // });
      };
      reader.onerror = (err) => {
        this.showError = 'Could not read file.';
        this.onUploadDone.emit(this.showError);
      };
      reader.readAsArrayBuffer(file); // Start reading the file
    }
  }

  deleteFileResult(result) {
    if (result) {
      if (this.selectedFile && this.fileForDelete.fileUrl === this.selectedFile.fileUrl) {
        this.selectedFile = null;
      }
      if (this.defaultCv.value && this.defaultCv.value && this.defaultCv.value === this.fileForDelete.fileUrl) {
        this.defaultCv.setValue(null);
      }
      this.blockView = true;
      this.userProxy.deleteUserFile(this.fileForDelete).then(async () => {
        this.onDelete.emit();
        await this.userProxy.getUser();
        this.blockView = false;
      });
    }
    this.fileForDelete = null;
  }

  deleteFile(file) {
    this.fileForDelete = file;
  }

  onChange(e, file) {
    this.selectedFile = file;
  }

  download(file) {
    this.downloadInProgress = true;
    this.apiService.downloadFile(file).pipe(delay(1000)).subscribe((data) => {
      const downloadURL = window.URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = downloadURL;
      link.download = file.name;
      link.click();
      setTimeout(() => {
        this.downloadInProgress = false;
        link.remove();
      }, 100);
    }); 
  }

  setDefaultCvNotifyConfirm(res) {
    if (res && this.setDefaultCvNotify) {
      this.userProxy.setTalentDefaultCv(this.setDefaultCvNotify);
      this.apiService.setDefaultCv(this.setDefaultCvNotify.fileUrl, this.setDefaultCvNotify.type).subscribe();
      this.defaultCv.setValue(this.setDefaultCvNotify.fileUrl);
      setTimeout(() => this.defaultCvFiles = this.getDefaultCvFiles(this.userFiles));
    }
    this.setDefaultCvNotify = null;
  }

  ngOnDestroy(): void {
    if (this.isAttach) {
      this.helperService.blockScroll$.next(false);
    }
    this.userFilesSub.unsubscribe();
  }

  private getDefaultCvFiles(files) {
    return files.filter(v => ['.doc', '.docx', '.pdf'].some(val => v.name.includes(val)))
      .map(item => ({ label: item.name, value: item.fileUrl }));
  }

}
